import type { SerializedArg } from '@logclip/types';
import { maskString, isSensitiveKeyName } from './mask.js';

const DEFAULT_MAX_BYTES = 8 * 1024; // 8 KB string cap (§6.1)
const MAX_WALK_NODES = 2000; // deep-walk node-visit cap (§6.1)

function byteLen(s: string): number {
  return new Blob([s]).size;
}

function truncate(s: string, maxBytes: number): { value: string; truncated: boolean } {
  if (byteLen(s) <= maxBytes) return { value: s, truncated: false };
  // Cheap char-based cut; good enough for previews.
  const approx = Math.max(16, Math.floor(maxBytes * 0.95));
  return { value: s.slice(0, approx), truncated: true };
}

/** Serialize a single console/track argument into a SerializedArg (§4, §6.1). */
export function serializeArg(arg: unknown, maxBytes = DEFAULT_MAX_BYTES): SerializedArg {
  if (arg === null) return { kind: 'primitive', value: null };
  const type = typeof arg;

  if (type === 'number' || type === 'boolean') {
    return { kind: 'primitive', value: arg as number | boolean };
  }

  if (type === 'string') {
    // Pre-cut giant strings (2× slack) so masking never regex-scans a multi-MB arg.
    const s = arg as string;
    const cut = s.length > maxBytes * 2 ? s.slice(0, maxBytes * 2) : s;
    const masked = maskString(cut);
    const { value, truncated } = truncate(masked, maxBytes);
    return { kind: 'string', value, truncated: truncated || cut !== s };
  }

  if (arg instanceof Error) {
    return {
      kind: 'error',
      name: arg.name,
      message: maskString(arg.message),
      stack: arg.stack ? maskString(arg.stack) : undefined,
    };
  }

  if (arg instanceof Element) {
    return { kind: 'dom', selector: cssPath(arg) };
  }

  if (type === 'object' || type === 'function' || type === 'symbol' || type === 'bigint') {
    // Budgets are enforced DURING the walk (node-visit count + ~2× the byte cap)
    // so a huge object never burns CPU/memory before the final truncate applies.
    const budget: WalkBudget = { nodes: MAX_WALK_NODES, bytes: maxBytes * 2, exhausted: false };
    const masked = maskValueBudgeted(arg, budget);
    let json: string | undefined;
    let truncated = budget.exhausted;
    try {
      const full = JSON.stringify(masked, safeReplacer());
      const cut = truncate(full ?? 'undefined', maxBytes);
      json = cut.value;
      truncated = truncated || cut.truncated;
    } catch {
      json = undefined;
    }
    return { kind: 'object', preview: previewOf(arg), json, truncated };
  }

  // undefined / fallback
  return { kind: 'primitive', value: String(arg) };
}

interface WalkBudget {
  nodes: number; // node visits left
  bytes: number; // approximate serialized bytes left
  exhausted: boolean;
}

/** `maskValue` with hard budgets enforced DURING the walk — a node-visit cap and a
 *  running approximate byte budget — so a huge arg (e.g. a multi-MB store) is never
 *  fully cloned/stringified just to be truncated afterwards. When a budget runs out
 *  we stop descending and substitute a '«truncated»' placeholder. Byte-identical to
 *  the unbudgeted walk for args that fit the budget. */
function maskValueBudgeted(value: unknown, budget: WalkBudget, depth = 0): unknown {
  if (budget.exhausted) return '«truncated»';
  if (depth > 6) return '«depth»';
  if (--budget.nodes <= 0) budget.exhausted = true;
  if (typeof value === 'string') {
    // Pre-cut giant strings so masking never regex-scans more than the budget.
    const cut = value.length > budget.bytes ? value.slice(0, Math.max(0, budget.bytes)) : value;
    if (cut !== value) budget.exhausted = true;
    const masked = maskString(cut);
    if ((budget.bytes -= masked.length + 2) <= 0) budget.exhausted = true;
    return masked;
  }
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    const out: unknown[] = [];
    for (const v of value) {
      if (budget.exhausted) {
        out.push('«truncated»');
        break;
      }
      out.push(maskValueBudgeted(v, budget, depth + 1));
    }
    return out;
  }
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(value)) {
    if (budget.exhausted) {
      out['…'] = '«truncated»';
      break;
    }
    if ((budget.bytes -= k.length + 4) <= 0) budget.exhausted = true;
    out[k] = isSensitiveKeyName(k)
      ? '«redacted»'
      : maskValueBudgeted((value as Record<string, unknown>)[k], budget, depth + 1);
  }
  return out;
}

function safeReplacer() {
  const seen = new WeakSet();
  return (_key: string, value: unknown) => {
    if (typeof value === 'bigint') return `${value.toString()}n`;
    if (typeof value === 'function') return `ƒ ${(value as { name?: string }).name || 'anonymous'}`;
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '«circular»';
      seen.add(value);
    }
    return value;
  };
}

function previewOf(value: unknown): string {
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (value && typeof value === 'object') {
    const keys = Object.keys(value as object);
    const head = keys.slice(0, 4).join(', ');
    return `{ ${head}${keys.length > 4 ? ', …' : ''} }`;
  }
  return String(value);
}

/**
 * Class names that change between builds or per-instance, so they must never
 * appear in a stored selector: CSS-in-JS hashes (emotion `css-1q2w3e`, styled-
 * components `sc-xyz`, CSS-module `Button_root__a1b2c`), and utility classes
 * carrying state that flips (`is-active`, `Mui-focused`).
 */
const UNSTABLE_CLASS =
  /^(css-[a-z0-9]{4,}|sc-[a-zA-Z0-9]{5,}|jsx-\d+|[\w-]+__[a-zA-Z0-9]{5,}|Mui(Focused|Selected|Disabled)|is-|has-|active$|selected$|open$|hover$)/;

/** Up to `n` stable classes for an element, in document order. */
function stableClasses(node: Element, n: number): string[] {
  return (node.getAttribute('class') || '')
    .trim()
    .split(/\s+/)
    .filter((c) => c && !UNSTABLE_CLASS.test(c) && c.length <= 40)
    .slice(0, n)
    .map((c) => `.${CSS.escape(c)}`);
}

/** 1-based index of `node` among siblings sharing its tag. */
function nthOfType(node: Element): number {
  let i = 1;
  let sib = node.previousElementSibling;
  while (sib) {
    if (sib.tagName === node.tagName) i++;
    sib = sib.previousElementSibling;
  }
  return i;
}

/** Does this selector match exactly the one node we mean? */
function isUnique(sel: string, target: Element, doc: Document): boolean {
  try {
    const m = doc.querySelectorAll(sel);
    return m.length === 1 && m[0] === target;
  } catch {
    return false;
  }
}

/**
 * A stable selector that resolves to EXACTLY ONE node.
 *
 * Uniqueness is the whole point and is why this verifies against the live DOM
 * rather than trusting a heuristic. The previous version walked ≤5 ancestors
 * taking only each element's FIRST class and never emitting a positional
 * qualifier, so on a component library it produced strings like
 * `…label.MuiFormControlLabel-root > span.MuiButtonBase-root > input…` that
 * matched every checkbox on the page. Downstream (the click heatmap) can only
 * do `querySelector`, so every one of those clicks was attributed to whichever
 * element happened to come first in the document — or dropped when nothing
 * matched at all.
 *
 * Strategy, cheapest first: an id short-circuits; otherwise build a descendant
 * chain of tag + stable classes, and escalate — add `:nth-of-type`, then walk
 * further up — until the selector is unique or we run out of ancestors. The
 * final fallback still carries positional qualifiers at every level, which is
 * unique by construction for any node in a static tree.
 */
export function cssPath(el: Element): string {
  const doc = el.ownerDocument || document;

  // An id is unique by definition and survives re-render — take it and stop.
  if (el.id && !/^\d/.test(el.id)) {
    const byId = `#${CSS.escape(el.id)}`;
    if (isUnique(byId, el, doc)) return byId;
  }

  // Ancestor chain, nearest first. 8 (not 5) because deep component trees need
  // the extra reach to become unambiguous.
  const chain: Element[] = [];
  for (let n: Element | null = el; n && n.nodeType === 1 && chain.length < 8; n = n.parentElement) {
    chain.push(n);
    if (n.id && !/^\d/.test(n.id)) break; // an id anchors the rest of the path
  }

  /** Render one level; `positional` adds :nth-of-type. */
  const part = (node: Element, positional: boolean): string => {
    if (node.id && !/^\d/.test(node.id)) return `#${CSS.escape(node.id)}`;
    let sel = node.tagName.toLowerCase();
    for (const c of stableClasses(node, 2)) sel += c;
    if (positional) sel += `:nth-of-type(${nthOfType(node)})`;
    return sel;
  };

  // Widen the window from the target outwards, and only then reach for
  // positional qualifiers — a class-based path is more readable and survives
  // sibling reordering, so prefer it when it is already unambiguous.
  for (const positional of [false, true]) {
    for (let depth = 1; depth <= chain.length; depth++) {
      const sel = chain
        .slice(0, depth)
        .reverse()
        .map((n) => part(n, positional))
        .join(' > ');
      if (isUnique(sel, el, doc)) return sel;
    }
  }

  // Nothing was unique (shadow DOM, duplicated ids, detached node): emit the
  // fully-qualified positional path anyway. It is the best available anchor and
  // the consumer records that it may be ambiguous.
  return chain
    .reverse()
    .map((n) => part(n, true))
    .join(' > ');
}

/** Parse the top frame of a stack into a source location. */
export function parseSource(stack?: string): { url?: string; line?: number; col?: number } | undefined {
  if (!stack) return undefined;
  const lines = stack.split('\n').map((l) => l.trim());
  for (const line of lines) {
    const m =
      line.match(/\((https?:\/\/[^)]+):(\d+):(\d+)\)/) ||
      line.match(/at (https?:\/\/\S+):(\d+):(\d+)/) ||
      line.match(/(https?:\/\/\S+):(\d+):(\d+)/);
    if (m) return { url: m[1], line: Number(m[2]), col: Number(m[3]) };
  }
  return undefined;
}

/**
 * True full-page dimensions, including content parked inside inner scroll
 * containers.
 *
 * `documentElement.scrollHeight` is the obvious answer and it is wrong for any
 * app-shell layout — a fixed-height flex frame with an `overflow:auto` region
 * (every Material-UI / admin dashboard). There the document never scrolls, so
 * scrollHeight is exactly one viewport tall no matter how long the content is.
 * Anything measuring that way sees, and renders, a single screenful.
 *
 * So: take the document's own scroll size, then for every scrollable element
 * consider where its content would END if it were laid out inline, and keep the
 * largest. Bounded to the first 400 elements with a scroll overflow so this
 * stays cheap on a click handler.
 */
let pmCache: { t: number; w: number; h: number } | null = null;
/** Re-measure at most this often. Page dimensions change on resize and on
 *  content load, not between two clicks a second apart. */
const PM_TTL_MS = 1000;

export function pageMetrics(doc: Document = document): { w: number; h: number } {
  const now = Date.now();
  if (pmCache && now - pmCache.t < PM_TTL_MS) return { w: pmCache.w, h: pmCache.h };

  const de = doc.documentElement;
  const body = doc.body;
  let w = Math.max(de?.scrollWidth || 0, body?.scrollWidth || 0, de?.clientWidth || 0);
  let h = Math.max(de?.scrollHeight || 0, body?.scrollHeight || 0, de?.clientHeight || 0);

  // The expensive scan is ONLY needed for app-shell layouts, where the document
  // itself never scrolls and its height is therefore about one viewport. On an
  // ordinary long page the document numbers above are already correct, and
  // walking the DOM reading scrollHeight — a forced layout per element — would
  // add tens of milliseconds to every click for nothing.
  const viewportH = de?.clientHeight || 0;
  const looksLikeAppShell = viewportH > 0 && h <= viewportH + 4;
  if (looksLikeAppShell) {
    try {
      let seen = 0;
      // Candidates only: elements that plausibly scroll. Far smaller than '*'.
      const nodes = doc.querySelectorAll<HTMLElement>('div,main,section,article,ul,ol,table,tbody');
      for (let i = 0; i < nodes.length && i < 1500; i++) {
        if (seen >= 60) break;
        const el = nodes[i]!;
        const sh = el.scrollHeight;
        if (sh <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1) continue;
        seen++;
        const r = el.getBoundingClientRect();
        h = Math.max(h, Math.round(r.top + (doc.defaultView?.scrollY ?? 0) + sh));
        w = Math.max(w, Math.round(r.left + (doc.defaultView?.scrollX ?? 0) + el.scrollWidth));
      }
    } catch {
      /* best effort — the document values above are already a usable floor */
    }
  }

  w = Math.max(1, w);
  h = Math.max(1, h);
  pmCache = { t: now, w, h };
  return { w, h };
}

/**
 * A click's position in full-page content space.
 *
 * `ev.pageX/Y` already fold in window scroll but know nothing about inner
 * scrollers, so a click on row 500 of a scrolled table reports the same
 * coordinates as row 1. Adding each scrollable ancestor's scroll offset maps
 * the point back onto the un-scrolled content, which is the space `pageMetrics`
 * measures — so the two agree.
 */
export function pagePoint(target: Element, clientX: number, clientY: number, view: Window = window): { x: number; y: number } {
  let sx = 0;
  let sy = 0;
  try {
    for (let n: Element | null = target; n && n !== view.document.documentElement; n = n.parentElement) {
      const el = n as HTMLElement;
      if (el.scrollHeight > el.clientHeight + 1) sy += el.scrollTop;
      if (el.scrollWidth > el.clientWidth + 1) sx += el.scrollLeft;
    }
  } catch {
    /* best effort */
  }
  return {
    x: Math.round(clientX + (view.scrollX || 0) + sx),
    y: Math.round(clientY + (view.scrollY || 0) + sy),
  };
}
